import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const { t } = useTranslation();
  const [state, setState] = useState({
    open: false, title: "", description: "", confirmLabel: "", danger: true,
  });
  const resolveRef = useRef(null);

  const confirm = useCallback(({ title, description, confirmLabel, danger = true } = {}) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        title: title || t("confirm.title"),
        description: description || t("confirm.description"),
        confirmLabel: confirmLabel || t("common.delete"),
        danger,
      });
      resolveRef.current = resolve;
    });
  }, [t]);

  const handle = (value) => {
    setState((s) => ({ ...s, open: false }));
    resolveRef.current && resolveRef.current(value);
    resolveRef.current = null;
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(o) => !o && handle(false)}>
        <AlertDialogContent className="bg-[#21252E] border-[#2E3241] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{state.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {state.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => handle(false)}
              data-testid="confirm-cancel"
              className="bg-[#171B24] border-[#2E3241] text-zinc-300 hover:bg-[#2A2F3A]"
            >
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handle(true)}
              data-testid="confirm-ok"
              className={state.danger
                ? "bg-red-500 hover:bg-red-600 text-white"
                : "bg-blue-500 hover:bg-blue-600 text-white"}
            >
              {state.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside ConfirmProvider");
  return ctx.confirm;
};
